import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
// import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { api } from '@/lib/api';
import { toast } from 'react-hot-toast';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from 'recharts';

interface AnalyticsData {
  totalAutomations: number;
  activeAutomations: number;
  totalExecutions: number;
  totalEmailsSent: number;
  totalEmailsFailed: number;
  averageSuccessRate: number;
  averageExecutionTime: number;
  topPerformingAutomations: Array<{
    id: string;
    name: string;
    successRate: number;
    emailsSent: number;
    executions: number;
  }>;
  dailyMetrics: Array<{
    date: string;
    executions: number;
    emailsSent: number;
    emailsFailed: number;
    successRate: number;
  }>;
  segmentPerformance: Array<{
    segmentName: string;
    emailsSent: number;
    successRate: number;
    openRate: number;
    clickRate: number;
  }>;
  triggerTypeDistribution: Array<{
    triggerType: string;
    count: number;
    percentage: number;
  }>;
  companyPerformance: Array<{
    companyName: string;
    automations: number;
    emailsSent: number;
    successRate: number;
  }>;
}

// interface AutomationDetails {
//   id: string;
//   name: string;
//   description?: string;
//   triggerType: string;
//   isActive: boolean;
//   stats: {
//     totalExecutions: number;
//     successfulExecutions: number;
//     failedExecutions: number;
//     emailsSent: number;
//     successRate: number;
//     averageExecutionTime: number;
//   };
//   recentExecutions: Array<{
//     id: string;
//     createdAt: string;
//     emailsSent: number;
//     emailsFailed: number;
//     executionTime: number;
//     status: string;
//   }>;
// }

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const EmailAnalyticsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  // const [automationDetails, setAutomationDetails] = useState<AutomationDetails | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadData();
  }, [selectedPeriod, selectedCompany]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [analyticsRes, companiesRes] = await Promise.all([
        api.get(`/email-analytics/dashboard?period=${selectedPeriod}&companyId=${selectedCompany}`),
        api.get('/companies?isActive=true'),
      ]);

      setAnalyticsData(analyticsRes.data.data);
      const companiesPayload = companiesRes.data?.data;
      const companiesList = Array.isArray(companiesPayload)
        ? companiesPayload
        : (companiesPayload?.companies ?? companiesPayload?.items ?? []);
      setCompanies(companiesList);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao carregar analytics');
    } finally {
      setLoading(false);
    }
  };

  // const loadAutomationDetails = async (automationId: string) => {
  //   try {
  //     const response = await api.get(`/email-automation/${automationId}/analytics?period=${selectedPeriod}`);
  //     setAutomationDetails(response.data.data);
  //   } catch (error: any) {
  //     toast.error(error.response?.data?.message || 'Erro ao carregar detalhes da automação');
  //   }
  // };

  // const getStatusBadge = (isActive: boolean) => (
  //   <Badge variant={isActive ? 'default' : 'secondary'}>
  //     {isActive ? 'Ativa' : 'Inativa'}
  //   </Badge>
  // );

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('pt-BR').format(num);
  };

  const formatPercentage = (num: number) => {
    return `${num.toFixed(1)}%`;
  };

  // const formatDate = (dateString: string) => {
  //   return new Date(dateString).toLocaleDateString('pt-BR');
  // };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Carregando analytics...</div>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Nenhum dado disponível</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Dashboard de Analytics</h1>
        <div className="flex gap-4">
          <Button variant="outline" onClick={() => navigate('/configuracoes')}>← Voltar para Configurações &gt; E-mails</Button>
          <Select
            value={selectedPeriod}
            onValueChange={setSelectedPeriod}
          >
            <option value="7d">Últimos 7 dias</option>
            <option value="30d">Últimos 30 dias</option>
            <option value="90d">Últimos 90 dias</option>
            <option value="1y">Último ano</option>
          </Select>
          <Select
            value={selectedCompany}
            onValueChange={setSelectedCompany}
          >
            <option value="all">Todas as empresas</option>
            {(Array.isArray(companies) ? companies : []).map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Visão Geral</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="segments">Segmentos</TabsTrigger>
          <TabsTrigger value="companies">Empresas</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Métricas Principais */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total de Automações</CardTitle>
                <Badge variant="outline">{analyticsData.totalAutomations}</Badge>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(analyticsData.totalAutomations)}</div>
                <p className="text-xs text-muted-foreground">
                  {analyticsData.activeAutomations} ativas
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Execuções</CardTitle>
                <Badge variant="outline">{formatNumber(analyticsData.totalExecutions)}</Badge>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(analyticsData.totalExecutions)}</div>
                <p className="text-xs text-muted-foreground">
                  Média por automação: {Math.round(analyticsData.totalExecutions / Math.max(analyticsData.totalAutomations, 1))}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Emails Enviados</CardTitle>
                <Badge variant="outline">{formatNumber(analyticsData.totalEmailsSent)}</Badge>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(analyticsData.totalEmailsSent)}</div>
                <p className="text-xs text-muted-foreground">
                  {formatNumber(analyticsData.totalEmailsFailed)} falharam
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Taxa de Sucesso</CardTitle>
                <Badge variant="outline">{formatPercentage(analyticsData.averageSuccessRate)}</Badge>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatPercentage(analyticsData.averageSuccessRate)}</div>
                <p className="text-xs text-muted-foreground">
                  Tempo médio: {Math.round(analyticsData.averageExecutionTime)}ms
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Gráficos */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Métricas Diárias */}
            <Card>
              <CardHeader>
                <CardTitle>Métricas Diárias</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={analyticsData.dailyMetrics}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="emailsSent"
                      stackId="1"
                      stroke="#8884d8"
                      fill="#8884d8"
                      name="Emails Enviados"
                    />
                    <Area
                      type="monotone"
                      dataKey="emailsFailed"
                      stackId="1"
                      stroke="#ff7300"
                      fill="#ff7300"
                      name="Emails Falhados"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Distribuição por Tipo de Trigger */}
            <Card>
              <CardHeader>
                <CardTitle>Distribuição por Tipo de Trigger</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={analyticsData.triggerTypeDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ triggerType, percentage }: any) => `${triggerType} ${percentage}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {analyticsData.triggerTypeDistribution.map((_entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Top Automações */}
          <Card>
            <CardHeader>
              <CardTitle>Top Automações por Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {analyticsData.topPerformingAutomations.map((automation, index) => (
                  <div key={automation.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <h4 className="font-medium">{automation.name}</h4>
                        <p className="text-sm text-muted-foreground">
                          {automation.executions} execuções • {formatNumber(automation.emailsSent)} emails
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-green-600">
                        {formatPercentage(automation.successRate)}
                      </div>
                      <p className="text-sm text-muted-foreground">Taxa de sucesso</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance" className="space-y-6">
          {/* Performance por Período */}
          <Card>
            <CardHeader>
              <CardTitle>Performance ao Longo do Tempo</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={analyticsData.dailyMetrics}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="successRate"
                    stroke="#00C49F"
                    strokeWidth={2}
                    name="Taxa de Sucesso (%)"
                  />
                  <Line
                    type="monotone"
                    dataKey="executions"
                    stroke="#0088FE"
                    strokeWidth={2}
                    name="Execuções"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Comparação de Performance */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Emails Enviados vs Falhados</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analyticsData.dailyMetrics}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="emailsSent" fill="#00C49F" name="Enviados" />
                    <Bar dataKey="emailsFailed" fill="#FF8042" name="Falhados" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Taxa de Sucesso Diária</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analyticsData.dailyMetrics}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="successRate" fill="#8884d8" name="Taxa de Sucesso (%)" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="segments" className="space-y-6">
          {/* Performance por Segmento */}
          <Card>
            <CardHeader>
              <CardTitle>Performance por Segmento</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {analyticsData.segmentPerformance.map((segment, index) => (
                  <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h4 className="font-medium">{segment.segmentName}</h4>
                      <p className="text-sm text-muted-foreground">
                        {formatNumber(segment.emailsSent)} emails enviados
                      </p>
                    </div>
                    <div className="flex gap-4 text-right">
                      <div>
                        <div className="text-lg font-bold text-green-600">
                          {formatPercentage(segment.successRate)}
                        </div>
                        <p className="text-sm text-muted-foreground">Sucesso</p>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-blue-600">
                          {formatPercentage(segment.openRate)}
                        </div>
                        <p className="text-sm text-muted-foreground">Abertura</p>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-purple-600">
                          {formatPercentage(segment.clickRate)}
                        </div>
                        <p className="text-sm text-muted-foreground">Cliques</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Gráfico de Segmentos */}
          <Card>
            <CardHeader>
              <CardTitle>Comparação de Segmentos</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={analyticsData.segmentPerformance}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="segmentName" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="successRate" fill="#00C49F" name="Taxa de Sucesso (%)" />
                  <Bar dataKey="openRate" fill="#0088FE" name="Taxa de Abertura (%)" />
                  <Bar dataKey="clickRate" fill="#FFBB28" name="Taxa de Cliques (%)" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="companies" className="space-y-6">
          {/* Performance por Empresa */}
          <Card>
            <CardHeader>
              <CardTitle>Performance por Empresa</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {analyticsData.companyPerformance.map((company, index) => (
                  <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h4 className="font-medium">{company.companyName}</h4>
                      <p className="text-sm text-muted-foreground">
                        {company.automations} automações • {formatNumber(company.emailsSent)} emails
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-green-600">
                        {formatPercentage(company.successRate)}
                      </div>
                      <p className="text-sm text-muted-foreground">Taxa de sucesso</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Gráfico de Empresas */}
          <Card>
            <CardHeader>
              <CardTitle>Comparação entre Empresas</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={analyticsData.companyPerformance}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="companyName" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="emailsSent" fill="#8884d8" name="Emails Enviados" />
                  <Bar dataKey="successRate" fill="#00C49F" name="Taxa de Sucesso (%)" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EmailAnalyticsDashboard;
